import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import {
  Target,
  Trophy,
  Users,
  Settings as SettingsIcon,
  Loader2,
  Search,
  X,
  Clock,
  AlertTriangle,
  Save,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const RATINGS = ['Top Performer', 'Good', 'Average', 'Needs Improvement'];
// Must match the DEPARTMENT_TASKS keys in pointing_system_routes.py.
const DEPARTMENTS = ['Preflight', 'CMD', 'Prepress', 'RIP QC', 'Other'];
const LEVELS = ['junior', 'mid', 'senior'];
const SEVERITIES = ['minor', 'major', 'critical'];

// Complexity levels in ascending order. Keys must match COMPLEXITY_ORDER in
// pointing_system_routes.py, which is what populates complexity_counts.
const COMPLEXITY_LEVELS = [
  { key: 'simple_simple', label: 'Simple Simple' },
  { key: 'simple', label: 'Simple' },
  { key: 'medium_simple', label: 'Medium Simple' },
  { key: 'medium', label: 'Medium' },
  { key: 'complex', label: 'Complex' },
  { key: 'super_complex', label: 'Super Complex' },
  { key: 'combined_job', label: 'Combined Job' },
  { key: 'rotary', label: 'Rotary' },
];

const ratingClass = (r) => {
  if (r === 'Top Performer') return 'bg-emerald-50 text-emerald-700';
  if (r === 'Good') return 'bg-blue-50 text-blue-700';
  if (r === 'Average') return 'bg-amber-50 text-amber-700';
  return 'bg-red-50 text-red-600';
};

const pctBar = (v, color) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden min-w-[60px]">
      <div className={`h-full ${color}`} style={{ width: `${Math.max(0, Math.min(100, v))}%` }} />
    </div>
    <span className="text-xs text-gray-600 w-11 text-right">{v}%</span>
  </div>
);

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const MANAGER_ROLES = ['admin', 'pm', 'preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead', 'epson_lead'];

const PointingSystem = () => {
  const { token, user } = useAuth();
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };
  const isAdmin = user?.role === 'admin';
  // Normal operators only ever see their own score — no ranked list, no
  // department roll-up, no settings.
  const canViewAll = MANAGER_ROLES.includes(user?.role);

  const [tab, setTab] = useState(canViewAll ? 'scoreboard' : 'me');
  const [startDate, setStartDate] = useState(daysAgoStr(30));
  const [endDate, setEndDate] = useState(todayStr());
  const [error, setError] = useState('');

  // ── Scoreboard ──────────────────────────────────────────────────────
  const [board, setBoard] = useState({ items: [], totals: {} });
  const [loading, setLoading] = useState(false);
  const [deptFilter, setDeptFilter] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [nameQuery, setNameQuery] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;

  const loadBoard = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await api.get('/pointing-system/scoreboard', {
        ...authHeader,
        params: {
          startDate, endDate,
          department: deptFilter || undefined,
          rating: ratingFilter || undefined,
          q: nameQuery || undefined,
        },
      });
      setBoard(res.data || { items: [], totals: {} });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load the scoreboard.');
      setBoard({ items: [], totals: {} });
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [startDate, endDate, deptFilter, ratingFilter, nameQuery]);

  useEffect(() => { if (tab === 'scoreboard') { setPage(1); loadBoard(); } }, [tab, loadBoard]);

  const pageItems = (board.items || []).slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil((board.items || []).length / PAGE_SIZE));

  // ── Operator drill-down ─────────────────────────────────────────────
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openOperator = async (uid) => {
    setDetailLoading(true); setDetail({ summary: null, rows: [] });
    try {
      const res = await api.get(`/pointing-system/operator/${uid}`, {
        ...authHeader, params: { startDate, endDate },
      });
      setDetail(res.data || { summary: null, rows: [] });
    } catch (err) {
      setDetail({ summary: null, rows: [], error: err.response?.data?.error || 'Failed to load details.' });
    } finally { setDetailLoading(false); }
  };

  // ── Departments ─────────────────────────────────────────────────────
  const [depts, setDepts] = useState([]);
  const [deptLoading, setDeptLoading] = useState(false);

  const loadDepts = useCallback(async () => {
    setDeptLoading(true);
    try {
      const res = await api.get('/pointing-system/departments', { ...authHeader, params: { startDate, endDate } });
      setDepts(res.data?.items || []);
    } catch { setDepts([]); } finally { setDeptLoading(false); }
    // eslint-disable-next-line
  }, [startDate, endDate]);

  useEffect(() => { if (tab === 'departments') loadDepts(); }, [tab, loadDepts]);

  // ── My Points (self-service) ────────────────────────────────────────
  const [mine, setMine] = useState(null);
  const [mineLoading, setMineLoading] = useState(false);

  const loadMine = useCallback(async () => {
    setMineLoading(true); setError('');
    try {
      const res = await api.get('/pointing-system/me', { ...authHeader, params: { startDate, endDate } });
      setMine(res.data || { summary: null, rows: [] });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load your points.');
      setMine({ summary: null, rows: [] });
    } finally { setMineLoading(false); }
    // eslint-disable-next-line
  }, [startDate, endDate]);

  useEffect(() => { if (tab === 'me') loadMine(); }, [tab, loadMine]);

  // ── Settings ────────────────────────────────────────────────────────
  const [cfg, setCfg] = useState(null);
  const [cats, setCats] = useState([]);
  const [users, setUsers] = useState([]);
  const [cfgLoading, setCfgLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  const loadCfg = useCallback(async () => {
    setCfgLoading(true);
    try {
      const res = await api.get('/pointing-system/config', authHeader);
      setCfg(res.data?.config || null);
      setCats(res.data?.error_categories || []);
      setUsers(res.data?.users || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load settings.');
    } finally { setCfgLoading(false); }
    // eslint-disable-next-line
  }, []);

  useEffect(() => { if (tab === 'settings' && !cfg) loadCfg(); }, [tab, cfg, loadCfg]);

  const setCfgPath = (path, value) => {
    setCfg((prev) => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      let node = next;
      for (let i = 0; i < path.length - 1; i += 1) {
        if (node[path[i]] === undefined || node[path[i]] === null) node[path[i]] = {};
        node = node[path[i]];
      }
      node[path[path.length - 1]] = value;
      return next;
    });
    setSaved(false);
  };

  const saveCfg = async () => {
    setSaving(true); setError('');
    try {
      const res = await api.put('/pointing-system/config', cfg, authHeader);
      setCfg(res.data?.config || cfg);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings.');
    } finally { setSaving(false); }
  };

  const num = (v) => (v === '' || v === null || v === undefined ? '' : Number(v));

  const TabBtn = ({ id, icon, label }) => (
    <button onClick={() => setTab(id)}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${tab === id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
      {icon} {label}
    </button>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <Target className="w-7 h-7 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Pointing System</h1>
          <p className="text-sm text-gray-500">
            {canViewAll
              ? 'Prepress Operations KPI — Productivity 50% · On-Time 30% · Quality 20%'
              : 'My performance — Productivity 50% · On-Time 30% · Quality 20%'}
          </p>
        </div>
      </div>

      {canViewAll && (
        <div className="flex gap-1 border-b border-gray-200 mb-5 overflow-x-auto">
          <TabBtn id="scoreboard" icon={<Trophy className="w-4 h-4" />} label="Scoreboard" />
          <TabBtn id="departments" icon={<Users className="w-4 h-4" />} label="Department Summary" />
          <TabBtn id="me" icon={<Target className="w-4 h-4" />} label="My Points" />
          <TabBtn id="settings" icon={<SettingsIcon className="w-4 h-4" />} label="Settings" />
        </div>
      )}

      {/* Date range (shared by scoreboard + departments) */}
      {tab !== 'settings' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-5">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            {tab === 'scoreboard' && canViewAll && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Department</label>
                  <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">All</option>
                    {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Rating</label>
                  <select value={ratingFilter} onChange={(e) => setRatingFilter(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">All</option>
                    {RATINGS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Operator</label>
                  <div className="relative">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input type="text" value={nameQuery} onChange={(e) => setNameQuery(e.target.value)}
                      placeholder="Search name…" className="border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm w-52" />
                  </div>
                </div>
              </>
            )}
            <button
              onClick={() => {
                if (tab === 'scoreboard') loadBoard();
                else if (tab === 'departments') loadDepts();
                else loadMine();
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm">Apply</button>
          </div>

          {/* Quick ranges. A CAPA penalty lands on a whole month, so month and
              year views are the ones that read correctly. */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className="text-xs text-gray-500">Quick:</span>
            {[
              ['This month', () => {
                const n = new Date();
                return [new Date(n.getFullYear(), n.getMonth(), 1), new Date(n.getFullYear(), n.getMonth() + 1, 0)];
              }],
              ['Last month', () => {
                const n = new Date();
                return [new Date(n.getFullYear(), n.getMonth() - 1, 1), new Date(n.getFullYear(), n.getMonth(), 0)];
              }],
              ['This year', () => {
                const n = new Date();
                return [new Date(n.getFullYear(), 0, 1), new Date(n.getFullYear(), 11, 31)];
              }],
              ['Last year', () => {
                const y = new Date().getFullYear() - 1;
                return [new Date(y, 0, 1), new Date(y, 11, 31)];
              }],
            ].map(([label, range]) => (
              <button
                key={label}
                onClick={() => {
                  const [from, to] = range();
                  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                  setStartDate(iso(from));
                  setEndDate(iso(to));
                }}
                className="px-2.5 py-1 text-xs border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
              >{label}</button>
            ))}
            <span className="text-xs text-gray-400">then press Apply</span>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-5 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{error}</span>
        </div>
      )}

      {/* ── MY POINTS (self-service) ───────────────────────────────── */}
      {tab === 'me' && (
        <>
          {mineLoading && <div className="text-center py-12 text-gray-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>}
          {!mineLoading && mine && !mine.summary && (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400">
              No completed tasks in this period.
            </div>
          )}
          {!mineLoading && mine && mine.summary && (
            <>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-5">
                <div className="flex flex-wrap items-center gap-4 mb-5">
                  <div>
                    <div className="text-xs text-gray-500">Your KPI Score</div>
                    <div className="text-4xl font-bold text-gray-800">{mine.summary.kpi_score}</div>
                  </div>
                  <span className={`text-sm rounded-full px-3 py-1 ${ratingClass(mine.summary.rating)}`}>{mine.summary.rating}</span>
                  <div className="ml-auto text-right">
                    <div className="text-xs text-gray-500">Points earned</div>
                    <div className="text-2xl font-bold text-gray-800">
                      {mine.summary.earned_points}
                      <span className="text-sm text-gray-400 font-normal"> / {mine.summary.target_points}</span>
                    </div>
                    <div className="text-xs text-gray-400 capitalize">{mine.summary.level} target · {mine.summary.active_days} active day(s)</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    ['Productivity', mine.summary.productivity_pct, 'bg-blue-500', `${mine.weights?.productivity ?? 50}% of KPI`],
                    ['On-Time', mine.summary.on_time_pct, 'bg-emerald-500', `${mine.weights?.on_time ?? 30}% of KPI`],
                    ['Quality', mine.summary.quality_pct, 'bg-amber-500', `${mine.weights?.quality ?? 20}% of KPI`],
                  ].map(([label, val, color, hint]) => (
                    <div key={label} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-baseline justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">{label}</span>
                        <span className="text-lg font-bold text-gray-800">{val}%</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full ${color}`} style={{ width: `${Math.max(0, Math.min(100, val))}%` }} />
                      </div>
                      <div className="text-xs text-gray-400 mt-1">{hint}</div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 pt-4 border-t border-gray-100">
                  {[
                    ['Jobs', mine.summary.total_jobs],
                    ['New jobs', mine.summary.new_jobs ?? 0],
                    ['Old jobs', mine.summary.old_jobs ?? 0],
                    ['Query jobs', mine.summary.query_jobs ?? 0],
                    ['External rejections', mine.summary.external_jobs ?? 0],
                    ['On time', mine.summary.on_time_jobs],
                    ['Late jobs', mine.summary.late_jobs],
                    ['Late logins', mine.summary.late_days ?? 0],
                    ['Break exceeded', mine.summary.break_exceed_days ?? 0],
                    ['Points lost to rework', mine.summary.lost_points],
                  ].map(([l, v]) => (
                    <div key={l}><div className="text-xs text-gray-500">{l}</div><div className="text-lg font-semibold text-gray-800">{v}</div></div>
                  ))}
                </div>

                <div className="mt-5 pt-4 border-t border-gray-100">
                  <div className="text-xs text-gray-500 mb-2">Jobs by complexity</div>
                  <div className="flex flex-wrap gap-2">
                    {COMPLEXITY_LEVELS.map(({ key, label }) => {
                      const n = (mine.summary.complexity_counts || {})[key] || 0;
                      return (
                        <span
                          key={key}
                          className={`px-2.5 py-1 rounded-lg border text-xs ${
                            n > 0
                              ? 'bg-blue-50 border-blue-200 text-blue-800'
                              : 'bg-gray-50 border-gray-200 text-gray-400'
                          }`}
                        >
                          {label} <span className="font-bold ml-1">{n}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-800 text-sm">My Jobs</h3>
                </div>
                <div className="overflow-x-auto max-h-[500px]">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Job</th>
                        <th className="text-left px-3 py-2 font-medium">Task</th>
                        <th className="text-right px-3 py-2 font-medium">Alloc (min)</th>
                        <th className="text-right px-3 py-2 font-medium">Actual (min)</th>
                        <th className="text-right px-3 py-2 font-medium">Points</th>
                        <th className="text-left px-3 py-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {mine.rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">No tasks.</td></tr>}
                      {mine.rows.map((r) => (
                        <tr key={r.task_id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-700">{r.job_number || '—'}<div className="text-xs text-gray-400">{r.client}</div></td>
                          <td className="px-3 py-2 text-gray-700">{r.task_name}{r.round > 1 && <span className="ml-1 text-xs bg-purple-50 text-purple-600 rounded px-1.5">R{r.round}</span>}</td>
                          <td className="px-3 py-2 text-right text-gray-500">{r.allocated_minutes}</td>
                          <td className={`px-3 py-2 text-right ${r.on_time ? 'text-gray-500' : 'text-red-600 font-medium'}`}>{r.actual_minutes}</td>
                          <td className="px-3 py-2 text-right font-semibold text-gray-800">
                            {r.earned_points}
                            {r.earned_points !== r.allocated_points && <span className="text-xs text-gray-400"> /{r.allocated_points}</span>}
                          </td>
                          <td className="px-3 py-2">
                            {r.on_time
                              ? <span className="text-xs bg-emerald-50 text-emerald-700 rounded px-2 py-0.5">On time</span>
                              : <span className="text-xs bg-red-50 text-red-600 rounded px-2 py-0.5 inline-flex items-center gap-1"><Clock className="w-3 h-3" />Late</span>}
                            {r.severity && <span className="ml-1 text-xs bg-amber-50 text-amber-700 rounded px-2 py-0.5 capitalize">{r.severity}</span>}
                            {r.note && <div className="text-xs text-gray-400 mt-0.5">{r.note}</div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ── SCOREBOARD ─────────────────────────────────────────────── */}
      {tab === 'scoreboard' && canViewAll && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            {[
              { label: 'Operators', value: board.totals?.operators ?? 0 },
              { label: 'Points Earned', value: board.totals?.earned_points ?? 0 },
              { label: 'Jobs', value: board.totals?.total_jobs ?? 0 },
              { label: 'Average KPI', value: `${board.totals?.avg_kpi ?? 0}%` },
            ].map((c) => (
              <div key={c.label} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="text-xs text-gray-500">{c.label}</div>
                <div className="text-2xl font-bold text-gray-800 mt-1">{c.value}</div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">#</th>
                    <th className="text-left px-3 py-2 font-medium">Operator</th>
                    <th className="text-left px-3 py-2 font-medium">Level</th>
                    <th className="text-left px-3 py-2 font-medium">Dept</th>
                    <th className="text-right px-3 py-2 font-medium">Points</th>
                    <th className="text-right px-3 py-2 font-medium">Target</th>
                    <th className="text-left px-3 py-2 font-medium w-32">Productivity</th>
                    <th className="text-left px-3 py-2 font-medium w-32">On-Time</th>
                    <th className="text-left px-3 py-2 font-medium w-32">Quality</th>
                    {/* Attendance sits outside the KPI columns on purpose —
                        it does not feed the score. */}
                    <th className="text-right px-3 py-2 font-medium" title="Days clocked in late">Late</th>
                    <th className="text-right px-3 py-2 font-medium" title="Days a break went over its limit">Brk+</th>
                    <th className="text-right px-3 py-2 font-medium" title="Points deducted by CAPA">CAPA</th>
                    <th className="text-right px-3 py-2 font-medium">KPI</th>
                    <th className="text-left px-3 py-2 font-medium">Rating</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading && <tr><td colSpan={14} className="px-3 py-10 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>}
                  {!loading && pageItems.length === 0 && (
                    <tr><td colSpan={14} className="px-3 py-10 text-center text-gray-400">No completed tasks in this period.</td></tr>
                  )}
                  {!loading && pageItems.map((r, i) => (
                    <tr key={r.user_id} onClick={() => openOperator(r.user_id)}
                      className="hover:bg-blue-50/40 cursor-pointer">
                      <td className="px-3 py-2 text-gray-400">{(page - 1) * PAGE_SIZE + i + 1}</td>
                      <td className="px-3 py-2 font-medium text-gray-800">{r.user_name}</td>
                      <td className="px-3 py-2"><span className="text-xs bg-gray-100 text-gray-600 rounded px-2 py-0.5 capitalize">{r.level}</span></td>
                      <td className="px-3 py-2 text-gray-600">{r.department || '—'}</td>
                      {/* When a CAPA has docked points, show the net figure —
                          that is what drives Productivity and the KPI. Showing
                          only the gross would make the row's own arithmetic
                          fail to reproduce the score beside it. */}
                      <td className="px-3 py-2 text-right font-semibold text-gray-800">
                        {r.capa_penalty_points ? (
                          <span title={`${r.earned_points} earned − ${r.capa_penalty_points} CAPA`}>
                            {r.net_points}
                            <span className="block text-[10px] font-normal text-gray-400 line-through">
                              {r.earned_points}
                            </span>
                          </span>
                        ) : r.earned_points}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-400">{r.target_points}</td>
                      <td className="px-3 py-2">{pctBar(r.productivity_pct, 'bg-blue-500')}</td>
                      <td className="px-3 py-2">{pctBar(r.on_time_pct, 'bg-emerald-500')}</td>
                      <td className="px-3 py-2">{pctBar(r.quality_pct, 'bg-amber-500')}</td>
                      <td className={`px-3 py-2 text-right ${r.late_days ? 'font-semibold text-amber-700' : 'text-gray-300'}`}>
                        {r.late_days || 0}
                      </td>
                      <td className={`px-3 py-2 text-right ${r.break_exceed_days ? 'font-semibold text-red-700' : 'text-gray-300'}`}>
                        {r.break_exceed_days || 0}
                      </td>
                      <td className={`px-3 py-2 text-right ${r.capa_penalty_points ? 'font-semibold text-red-700' : 'text-gray-300'}`}
                        title={r.capa_penalty_points
                          ? `${r.capa_count} CAPA(s): ${r.earned_points} earned → ${r.net_points} net`
                          : ''}>
                        {r.capa_penalty_points ? `−${r.capa_penalty_points}` : 0}
                      </td>
                      <td className="px-3 py-2 text-right font-bold text-gray-800">{r.kpi_score}</td>
                      <td className="px-3 py-2"><span className={`text-xs rounded-full px-2 py-0.5 whitespace-nowrap ${ratingClass(r.rating)}`}>{r.rating}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                <div className="flex gap-2">
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50"><ChevronLeft className="w-4 h-4" /> Prev</button>
                  <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50">Next <ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-3">Click an operator to see their job-by-job point breakdown.</p>
        </>
      )}

      {/* ── DEPARTMENTS ────────────────────────────────────────────── */}
      {tab === 'departments' && canViewAll && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Department</th>
                  <th className="text-right px-3 py-2 font-medium">Operators</th>
                  <th className="text-right px-3 py-2 font-medium">Jobs</th>
                  <th className="text-right px-3 py-2 font-medium">Points Earned</th>
                  <th className="text-right px-3 py-2 font-medium">Allocated</th>
                  <th className="text-right px-3 py-2 font-medium">Avg / Operator</th>
                  <th className="text-left px-3 py-2 font-medium w-36">On-Time</th>
                  <th className="text-right px-3 py-2 font-medium">Avg KPI</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {deptLoading && <tr><td colSpan={8} className="px-3 py-10 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>}
                {!deptLoading && depts.length === 0 && <tr><td colSpan={8} className="px-3 py-10 text-center text-gray-400">No data for this period.</td></tr>}
                {!deptLoading && depts.map((d) => (
                  <tr key={d.department} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-800">{d.department}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{d.operators}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{d.jobs}</td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-800">{d.earned_points}</td>
                    <td className="px-3 py-2 text-right text-gray-400">{d.allocated_points}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{d.avg_points_per_operator}</td>
                    <td className="px-3 py-2">{pctBar(d.on_time_pct, 'bg-emerald-500')}</td>
                    <td className="px-3 py-2 text-right font-bold text-gray-800">{d.avg_kpi}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── SETTINGS ───────────────────────────────────────────────── */}
      {tab === 'settings' && canViewAll && (
        <div className="space-y-5">
          {cfgLoading && <div className="text-center py-10 text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
          {!cfgLoading && cfg && (
            <>
              {!isAdmin && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-2 text-sm">
                  View only — an administrator can change these values.
                </div>
              )}

              {/* Point basis + targets */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-800 mb-1">Point Basis &amp; Daily Targets</h3>
                <p className="text-xs text-gray-500 mb-4">1 point = {cfg.minutes_per_point} minutes · {cfg.working_hours_per_day} hr day = {Math.round((cfg.working_hours_per_day * 60) / (cfg.minutes_per_point || 5))} points</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Minutes per point</label>
                    <input type="number" min="1" disabled={!isAdmin} value={num(cfg.minutes_per_point)}
                      onChange={(e) => setCfgPath(['minutes_per_point'], Number(e.target.value))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Working hours / day</label>
                    <input type="number" min="1" disabled={!isAdmin} value={num(cfg.working_hours_per_day)}
                      onChange={(e) => setCfgPath(['working_hours_per_day'], Number(e.target.value))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {LEVELS.map((lv) => (
                    <div key={lv} className="border border-gray-200 rounded-lg p-3">
                      <div className="text-sm font-medium text-gray-700 capitalize mb-2">{lv}</div>
                      <div className="flex items-center gap-2">
                        <input type="number" disabled={!isAdmin} value={num(cfg.targets?.[lv]?.min)}
                          onChange={(e) => setCfgPath(['targets', lv, 'min'], Number(e.target.value))}
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm disabled:bg-gray-50" />
                        <span className="text-gray-400 text-sm">to</span>
                        <input type="number" disabled={!isAdmin} value={num(cfg.targets?.[lv]?.max)}
                          onChange={(e) => setCfgPath(['targets', lv, 'max'], Number(e.target.value))}
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm disabled:bg-gray-50" />
                      </div>
                      <div className="text-xs text-gray-400 mt-1">points / day</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Weights */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-800 mb-1">KPI Weighting</h3>
                <p className="text-xs text-gray-500 mb-4">Must total 100%.</p>
                <div className="grid grid-cols-3 gap-4">
                  {[['productivity', 'Productivity'], ['on_time', 'On-Time'], ['quality', 'Quality']].map(([k, label]) => (
                    <div key={k}>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{label} %</label>
                      <input type="number" disabled={!isAdmin} value={num(cfg.weights?.[k])}
                        onChange={(e) => setCfgPath(['weights', k], Number(e.target.value))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" />
                    </div>
                  ))}
                </div>
                <div className="text-xs mt-2 text-gray-500">
                  Total: {(Number(cfg.weights?.productivity || 0) + Number(cfg.weights?.on_time || 0) + Number(cfg.weights?.quality || 0))}%
                </div>
              </div>

              {/* Rework split + penalties */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-800 mb-1">Rework Distribution &amp; Quality Penalty</h3>
                <p className="text-xs text-gray-500 mb-4">
                  When another operator fixes an error the points are split. A same-operator rework earns no additional points.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Severity</th>
                        <th className="text-left px-3 py-2 font-medium">Original operator %</th>
                        <th className="text-left px-3 py-2 font-medium">Rework operator %</th>
                        <th className="text-left px-3 py-2 font-medium">Quality penalty %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {SEVERITIES.map((sev) => (
                        <tr key={sev}>
                          <td className="px-3 py-2 capitalize font-medium text-gray-700">{sev}</td>
                          <td className="px-3 py-2">
                            <input type="number" disabled={!isAdmin} value={num(cfg.rework_split?.[sev]?.original)}
                              onChange={(e) => setCfgPath(['rework_split', sev, 'original'], Number(e.target.value))}
                              className="w-24 border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-50" />
                          </td>
                          <td className="px-3 py-2">
                            <input type="number" disabled={!isAdmin} value={num(cfg.rework_split?.[sev]?.rework)}
                              onChange={(e) => setCfgPath(['rework_split', sev, 'rework'], Number(e.target.value))}
                              className="w-24 border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-50" />
                          </td>
                          <td className="px-3 py-2">
                            <input type="number" disabled={!isAdmin} value={num(cfg.quality_penalty?.[sev])}
                              onChange={(e) => setCfgPath(['quality_penalty', sev], Number(e.target.value))}
                              className="w-24 border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-50" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Excluded tasks */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-800 mb-1">Excluded Tasks</h3>
                <p className="text-xs text-gray-500 mb-3">
                  These steps are left out of the KPI entirely — no points, and they don't count towards
                  on-time or quality. Approval steps are waiting time on someone else, not operator work.
                  One task name per line.
                </p>
                <textarea
                  disabled={!isAdmin}
                  rows={4}
                  value={(cfg.excluded_tasks || []).join('\n')}
                  onChange={(e) => setCfgPath(['excluded_tasks'], e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono disabled:bg-gray-50"
                  placeholder={'Layout approval\nSingle-up approval'}
                />
              </div>

              {/* Severity mapping */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-800 mb-1">Error Category Severity</h3>
                <p className="text-xs text-gray-500 mb-4">Unmapped categories are treated as Minor.</p>
                {cats.length === 0 && <div className="text-sm text-gray-400">No error categories configured yet.</div>}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-80 overflow-auto">
                  {cats.map((c) => (
                    <div key={c.id} className="flex items-center gap-3 border border-gray-200 rounded-lg px-3 py-2">
                      <span className="flex-1 text-sm text-gray-700 truncate" title={c.name}>{c.name}</span>
                      <select disabled={!isAdmin}
                        value={(cfg.severity_map || {})[c.name] || 'minor'}
                        onChange={(e) => setCfgPath(['severity_map', c.name], e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-sm capitalize disabled:bg-gray-50">
                        {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Operator levels */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-800 mb-1">Operator Levels</h3>
                <p className="text-xs text-gray-500 mb-3">
                  Levels come from the Skill Matrix proficiency (beginner → Junior, intermediate → Mid, expert → Senior).
                  Choose a value here to override an individual.
                </p>
                <div className="relative mb-3 max-w-xs">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input type="text" value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search user…" className="border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm w-full" />
                </div>
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-96 overflow-auto">
                  {users
                    .filter((u) => !userSearch || u.name.toLowerCase().includes(userSearch.toLowerCase()))
                    .map((u) => {
                      const override = (cfg.user_levels || {})[u.id] || '';
                      return (
                        <div key={u.id} className="flex items-center gap-3 px-3 py-2">
                          <span className="flex-1 text-sm text-gray-700">{u.name}</span>
                          <span className="text-xs text-gray-400 w-28">{u.role}</span>
                          <span className="text-xs text-gray-400 w-32 capitalize">
                            {u.skill_proficiency ? `skill: ${u.skill_proficiency}` : 'no skill entry'}
                          </span>
                          <select disabled={!isAdmin} value={override}
                            onChange={(e) => {
                              const next = { ...(cfg.user_levels || {}) };
                              if (e.target.value) next[u.id] = e.target.value; else delete next[u.id];
                              setCfgPath(['user_levels'], next);
                            }}
                            className="border border-gray-300 rounded px-2 py-1 text-sm capitalize disabled:bg-gray-50 w-40">
                            <option value="">Auto ({u.level})</option>
                            {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                          </select>
                        </div>
                      );
                    })}
                  {users.length === 0 && <div className="px-3 py-6 text-center text-sm text-gray-400">No users found.</div>}
                </div>
              </div>

              {isAdmin && (
                <div className="flex items-center gap-3">
                  <button onClick={saveCfg} disabled={saving}
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-5 py-2 rounded-lg text-sm font-medium">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Settings'}
                  </button>
                  <button onClick={() => { setCfg(null); loadCfg(); }} className="text-sm text-gray-500 hover:text-gray-700">Reset</button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── OPERATOR DRILL-DOWN MODAL ──────────────────────────────── */}
      {detail && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h3 className="font-semibold text-gray-800">{detail.summary?.user_name || 'Operator'}</h3>
                <p className="text-xs text-gray-500">
                  {startDate} → {endDate}
                  {detail.summary && ` · ${detail.summary.level} · ${detail.summary.total_jobs} jobs`}
                </p>
              </div>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>

            {detailLoading && <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>}

            {!detailLoading && detail.summary && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 px-5 py-4 bg-gray-50 border-b border-gray-200">
                  {[
                    ['Points', detail.summary.earned_points],
                    ['Target', detail.summary.target_points],
                    ['Productivity', `${detail.summary.productivity_pct}%`],
                    ['On-Time', `${detail.summary.on_time_pct}%`],
                    ['Quality', `${detail.summary.quality_pct}%`],
                  ].map(([l, v]) => (
                    <div key={l}><div className="text-xs text-gray-500">{l}</div><div className="text-lg font-bold text-gray-800">{v}</div></div>
                  ))}
                </div>
                {/* New vs old jobs. Round 1 is the first pass at a job; a later
                    round means it came back for rework. */}
                <div className="grid grid-cols-3 gap-3 px-5 py-4 bg-white border-b border-gray-200">
                  <div>
                    <div className="text-xs text-gray-500">New jobs</div>
                    <div className="text-lg font-bold text-green-700">{detail.summary.new_jobs ?? 0}</div>
                    <div className="text-[11px] text-gray-400">Round 1</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Old jobs</div>
                    <div className="text-lg font-bold text-amber-700">{detail.summary.old_jobs ?? 0}</div>
                    <div className="text-[11px] text-gray-400">Round 2 and later</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Total jobs</div>
                    <div className="text-lg font-bold text-gray-800">{detail.summary.total_jobs ?? 0}</div>
                    <div className="text-[11px] text-gray-400">
                      {detail.summary.total_jobs
                        ? `${Math.round(((detail.summary.old_jobs || 0) / detail.summary.total_jobs) * 100)}% rework`
                        : '—'}
                    </div>
                  </div>
                </div>

                {/* Rejections. Internally raised ones are queries; external
                    ones came back from the client. Counted per job, so a job
                    with several error categories still counts once. */}
                <div className="grid grid-cols-3 gap-3 px-5 py-4 bg-white border-b border-gray-200">
                  <div>
                    <div className="text-xs text-gray-500">Query jobs</div>
                    <div className="text-lg font-bold text-orange-700">{detail.summary.query_jobs ?? 0}</div>
                    <div className="text-[11px] text-gray-400">Internal rejection</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">External rejections</div>
                    <div className="text-lg font-bold text-red-700">{detail.summary.external_jobs ?? 0}</div>
                    <div className="text-[11px] text-gray-400">Returned by client</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Rejection rate</div>
                    <div className="text-lg font-bold text-gray-800">
                      {detail.summary.total_jobs
                        ? `${Math.round((((detail.summary.query_jobs || 0) + (detail.summary.external_jobs || 0)) / detail.summary.total_jobs) * 100)}%`
                        : '—'}
                    </div>
                    <div className="text-[11px] text-gray-400">Of total jobs</div>
                  </div>
                </div>

                {/* Attendance. Shown alongside the KPI but NOT part of it —
                    the score is productivity, on-time and quality, and folding
                    attendance in would rewrite every past score. */}
                <div className="grid grid-cols-3 gap-3 px-5 py-4 bg-white border-b border-gray-200">
                  <div>
                    <div className="text-xs text-gray-500">Late logins</div>
                    <div className="text-lg font-bold text-amber-700">{detail.summary.late_days ?? 0}</div>
                    <div className="text-[11px] text-gray-400">
                      {detail.summary.late_minutes_total
                        ? `${detail.summary.late_minutes_total} min total`
                        : 'days late'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Break exceeded</div>
                    <div className="text-lg font-bold text-red-700">{detail.summary.break_exceed_days ?? 0}</div>
                    <div className="text-[11px] text-gray-400">
                      {detail.summary.break_over_total
                        ? `${detail.summary.break_over_total} min over`
                        : 'days over limit'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Days worked</div>
                    <div className="text-lg font-bold text-gray-800">{detail.summary.active_days ?? 0}</div>
                    <div className="text-[11px] text-gray-400">With completed jobs</div>
                  </div>
                </div>

                {/* CAPA. Shown only when there is one — an empty row on every
                    operator's drawer would just be noise. */}
                {(detail.summary.capa_penalty_points ?? 0) > 0 && (
                  <div className="px-5 py-4 bg-red-50 border-b border-red-200">
                    <div className="flex items-baseline justify-between">
                      <div>
                        <div className="text-xs text-red-700 font-semibold">CAPA penalty</div>
                        <div className="text-[11px] text-red-600">
                          {detail.summary.capa_count} CAPA{detail.summary.capa_count === 1 ? '' : 's'} in this period
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-red-700">
                          &minus;{detail.summary.capa_penalty_points}
                        </div>
                        <div className="text-[11px] text-gray-500">
                          {detail.summary.earned_points} earned &rarr; {detail.summary.net_points} net
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 space-y-1">
                      {(detail.summary.capas || []).map((c) => (
                        <div key={c.capa_id} className="text-[11px] text-red-800 flex flex-wrap gap-x-2">
                          <span className="font-semibold">{c.capa_number}</span>
                          <span>{c.job_code}</span>
                          <span className="text-red-600">{c.task_name}</span>
                          <span>{c.base_points} pts &rarr; &minus;{c.penalty_points}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Jobs by complexity. Levels with no jobs are dimmed rather than
                    hidden, so the spread is comparable between operators. */}
                <div className="px-5 py-4 bg-white border-b border-gray-200">
                  <div className="text-xs text-gray-500 mb-2">Jobs by complexity</div>
                  <div className="flex flex-wrap gap-2">
                    {COMPLEXITY_LEVELS.map(({ key, label }) => {
                      const n = (detail.summary.complexity_counts || {})[key] || 0;
                      return (
                        <span
                          key={key}
                          className={`px-2.5 py-1 rounded-lg border text-xs ${
                            n > 0
                              ? 'bg-blue-50 border-blue-200 text-blue-800'
                              : 'bg-gray-50 border-gray-200 text-gray-400'
                          }`}
                        >
                          {label} <span className="font-bold ml-1">{n}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>

                <div className="px-5 py-3 flex items-center gap-3 border-b border-gray-100">
                  <span className="text-sm text-gray-600">KPI Score</span>
                  <span className="text-2xl font-bold text-gray-800">{detail.summary.kpi_score}</span>
                  <span className={`text-xs rounded-full px-2 py-0.5 ${ratingClass(detail.summary.rating)}`}>{detail.summary.rating}</span>
                  {detail.summary.lost_points > 0 && (
                    <span className="text-xs text-red-600 ml-auto">−{detail.summary.lost_points} pts lost to rework</span>
                  )}
                </div>
                <div className="overflow-auto flex-1">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Job</th>
                        <th className="text-left px-3 py-2 font-medium">Task</th>
                        <th className="text-left px-3 py-2 font-medium">Complexity</th>
                        <th className="text-right px-3 py-2 font-medium">Alloc (min)</th>
                        <th className="text-right px-3 py-2 font-medium">Actual (min)</th>
                        <th className="text-right px-3 py-2 font-medium">Points</th>
                        <th className="text-left px-3 py-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {detail.rows.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">No tasks in this period.</td></tr>}
                      {detail.rows.map((r) => (
                        <tr key={r.task_id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-700">{r.job_number || '—'}<div className="text-xs text-gray-400">{r.client}</div></td>
                          <td className="px-3 py-2 text-gray-700">{r.task_name}{r.round > 1 && <span className="ml-1 text-xs bg-purple-50 text-purple-600 rounded px-1.5">R{r.round}</span>}</td>
                          <td className="px-3 py-2 text-gray-500 capitalize">{(r.complexity || '').replace(/_/g, ' ')}</td>
                          <td className="px-3 py-2 text-right text-gray-500">{r.allocated_minutes}</td>
                          <td className={`px-3 py-2 text-right ${r.on_time ? 'text-gray-500' : 'text-red-600 font-medium'}`}>{r.actual_minutes}</td>
                          <td className="px-3 py-2 text-right font-semibold text-gray-800">
                            {r.earned_points}
                            {r.earned_points !== r.allocated_points && <span className="text-xs text-gray-400"> /{r.allocated_points}</span>}
                          </td>
                          <td className="px-3 py-2">
                            {r.on_time
                              ? <span className="text-xs bg-emerald-50 text-emerald-700 rounded px-2 py-0.5">On time</span>
                              : <span className="text-xs bg-red-50 text-red-600 rounded px-2 py-0.5 inline-flex items-center gap-1"><Clock className="w-3 h-3" />Late</span>}
                            {r.severity && <span className="ml-1 text-xs bg-amber-50 text-amber-700 rounded px-2 py-0.5 capitalize">{r.severity}</span>}
                            {r.note && <div className="text-xs text-gray-400 mt-0.5">{r.note}</div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {!detailLoading && !detail.summary && (
              <div className="p-10 text-center text-gray-400">{detail.error || 'No data for this operator in the selected period.'}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PointingSystem;
